<script setup lang="ts">
import { computed } from 'vue'
import { useExtendedI18n } from '@/i18n'
import { minus, toNumber, type Money } from '@/modules/shared/utils/money'
import { VButton, VCard, VCardTitle, VDescriptionListDetailed, VMetricCard } from '@/modules/shared/components'
import type { DescriptionListDetailedItem } from '@/modules/shared/components/types'
import { ArrowDownToLine, Briefcase, Signature } from 'lucide-vue-next'

const props = defineProps<{
  entityType: 'spv' | 'fund'
  isAdmin: boolean
  totalCommitted: Money
  targetRaise: Money
  investorCount: number
  paidCalled: Money
  totalCalled: Money
  docsSigned: number
  docsTotal: number
  investmentCompletedCount: number
}>()

const emit = defineEmits<{
  'add-commitment': []
  'action-click': [item: DescriptionListDetailedItem]
}>()

const { n } = useExtendedI18n()

const totalCommittedAmount = computed(() => toNumber(props.totalCommitted))
const targetRaiseAmount = computed(() => toNumber(props.targetRaise))

const committedPercent = computed(() =>
  targetRaiseAmount.value > 0
    ? Math.min(Math.round((totalCommittedAmount.value / targetRaiseAmount.value) * 100), 100)
    : 0,
)

const remaining = computed<Money>(() => {
  const diff = minus(props.targetRaise, props.totalCommitted)
  return toNumber(diff) > 0 ? diff : { ...props.totalCommitted, amount: '0.00' }
})

const metricValue = computed(() => n(props.totalCommitted, 'currency'))

const committedBar = computed(() => ({
  value: n(props.totalCommitted, 'currency'),
  width: committedPercent.value,
  color: '#2E8B91',
  label: 'Committed',
}))

const remainingBar = computed(() => {
  if (committedPercent.value >= 100) return undefined
  return {
    value: n(remaining.value, 'currency'),
    width: 100,
    color: '#D2D5DB',
    label: 'Remaining',
    style: 'solid' as const,
  }
})

const totalBar = computed(() => ({
  value: n(props.targetRaise, 'currency'),
  label: 'Target',
}))

const activityRows = computed<DescriptionListDetailedItem[]>(() => {
  const rows: DescriptionListDetailedItem[] = [
    {
      icon: Signature,
      label: 'Documents Signed',
      sublabel: 'agreements executed by investors',
      value: `${props.docsSigned} / ${props.docsTotal}`,
      color: '#64748B',
      ...(props.isAdmin ? { actionLabel: 'Request Signatures' } : {}),
    },
    {
      icon: ArrowDownToLine,
      label: 'Capital Paid',
      sublabel: 'received from investors',
      value: n(props.paidCalled, 'currency'),
      color: '#3092b3',
      ...(props.isAdmin ? { actionLabel: 'Call Capital' } : {}),
    },
    {
      icon: Briefcase,
      label: 'Investment Completed',
      sublabel: 'capital deployed into target company',
      value: props.investmentCompletedCount > 0 ? 'Yes' : 'No',
      color: '#059669',
      ...(props.isAdmin && props.investmentCompletedCount === 0 ? { actionLabel: 'Complete Investment' } : {}),
    },
  ]
  return rows
})
</script>

<template>
  <VCard>
    <VCardTitle title="Raise Summary" />
    <VMetricCard
      label="Committed"
      :value="metricValue"
      color="#2E8B91"
      :paid="committedBar"
      :pending="remainingBar"
      :total="totalBar"
      class="mb-6"
    />
    <VButton v-if="isAdmin" size="md" variant="v-teal" class="mt-4 w-full" @click="emit('add-commitment')">
      Add Commitment
    </VButton>

    <div class="mt-5 border-t pt-5">
      <VDescriptionListDetailed :items="activityRows" @action-click="emit('action-click', $event)" />
    </div>
  </VCard>
</template>
